import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {BreadcrumbName} from 'common/src/modules/breadcrumbs/breadcrumbs.enums';
import {SharePermission} from 'common/src/modules/sharing/sharing.types';
import {CreativeTemplate} from '../../../creativeTemplates.types';
import {CreativeTemplateBaseState} from './creativeTemplateBase.types';
import creativeTemplateMappingReducer, {
  templateMappingInitialState,
} from '../templateMapping/templateMapping.slice';
import creativeTemplateTrackChangesReducer, {
  creativeTemplateTrackChangesInitialState,
} from '../creativeTemplateTrackChanges/creativeTemplateTrackChanges.slice';
import creativeTemplateFormatMoviesReducer, {
  formatMoviesInitialState,
} from '../formatMovies/formatMovies.slice';
import {Widget} from 'common/src/modules/dynamicParameters/widgets/widgets.types';
import templateFontsReducer, {templateFontsInitialState} from '../templateFonts/templateFonts.slice';
import templateAssetsReducer from '../templateAssets/templateAssets.slice';
import {initialAssetDisplayState as templateAssetsInitialState} from 'common/src/modules/Assets/Assets.types';

const creativeTemplateBaseInitialState: CreativeTemplateBaseState = {
  template: null,
  sharePermissions: [],
  templateTearsheetName: '',
  loading: true,
  mapping: templateMappingInitialState,
  formatMovies: formatMoviesInitialState,
  trackChanges: creativeTemplateTrackChangesInitialState,
  templateFonts: templateFontsInitialState,
  templateAssets: templateAssetsInitialState,
  widgets: [],
};

const creativeTemplateBaseSlice = createSlice({
  name: BreadcrumbName.CreativeTemplatePage,
  initialState: creativeTemplateBaseInitialState,
  reducers: {
    resetState: () => creativeTemplateBaseInitialState,
    setTemplatePageData: (
      state,
      action: PayloadAction<{template: CreativeTemplate; sharePermissions: SharePermission[]}>,
    ) => {
      state.template = action.payload.template;
      state.sharePermissions = action.payload.sharePermissions;
    },

    updateTemplate: (state, action: PayloadAction<Partial<CreativeTemplate>>) => {
      if (!state.template) return;
      state.template = {...state.template, ...action.payload};
    },

    updateTemplateTearsheetName: (state, action: PayloadAction<string>) => {
      state.templateTearsheetName = action.payload;
    },

    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setWidgets: (state, action: PayloadAction<Widget[]>) => {
      state.widgets = action.payload;
    },
  },
});

export const {
  resetState,
  setTemplatePageData,
  updateTemplate,
  updateTemplateTearsheetName,
  setLoading,
  setWidgets: setTemplateWidgets,
} = creativeTemplateBaseSlice.actions;

// Combined reducer that merges base properties and delegates nested slices
export default function creativeTemplateBaseReducer(
  state: CreativeTemplateBaseState,
  action: PayloadAction,
): CreativeTemplateBaseState {
  const baseState = creativeTemplateBaseSlice.reducer(state, action);

  const mappingState = creativeTemplateMappingReducer(baseState.mapping, action);
  const formatMoviesState = creativeTemplateFormatMoviesReducer(baseState.formatMovies, action);
  const trackChangesState = creativeTemplateTrackChangesReducer(baseState.trackChanges, action);
  const templateFontsState = templateFontsReducer(baseState.templateFonts, action);
  const templateAssetsState = templateAssetsReducer(baseState.templateAssets, action);

  return {
    ...baseState,
    mapping: mappingState,
    formatMovies: formatMoviesState,
    trackChanges: trackChangesState,
    templateFonts: templateFontsState,
    templateAssets: templateAssetsState,
  };
}
